import type { AiComponentDto } from '@terra/shared-contracts-ai-applications';
import { isMcpProtocolMethodVisibleInAssets } from '@terra/shared-utils';
import { isAiComponentVisibleInAssets } from './aiComponentVerification';
import type { AiComponentCodeCoverageFilter } from './aiComponentVerification';
import { parseAiComponentMetadata } from './aiComponentMetadata';

const TOOL_FAMILY_COMPONENT_TYPES = new Set<AiComponentDto['type']>([
  'TOOL',
  'MCP_TOOL',
  'MCP_PROMPT',
  'MCP_RESOURCE',
  'MCP_PROTOCOL',
]);

export function isToolFamilyComponentType(
  type: AiComponentDto['type'],
): boolean {
  return TOOL_FAMILY_COMPONENT_TYPES.has(type);
}

export function isUserFacingAgent(component: AiComponentDto): boolean {
  return component.type === 'AGENT' || component.type === 'SUB_AGENT';
}

export function countAgentComponents(components: AiComponentDto[]): number {
  return components.filter(isUserFacingAgent).length;
}

export function isSupportingComponent(component: AiComponentDto): boolean {
  return component.type !== 'AGENT' && component.type !== 'SUB_AGENT';
}

export function countSupportingComponents(
  components: AiComponentDto[],
): number {
  return components.filter(isSupportingComponent).length;
}

export function isAssetsComponentsTabItem(component: AiComponentDto): boolean {
  return isSupportingComponent(component);
}

export function isMcpProtocolAssetVisible(component: AiComponentDto): boolean {
  if (component.type !== 'MCP_PROTOCOL') {
    return true;
  }

  const probeStatus = parseAiComponentMetadata(component.metadata).probeStatus;
  return (
    typeof probeStatus === 'string' &&
    isMcpProtocolMethodVisibleInAssets(probeStatus)
  );
}

export function isComponentsTabVisibleItem({
  component,
  isMcpApplication = false,
  hideMcpProtocolMethods,
}: {
  component: AiComponentDto;
  isMcpApplication?: boolean;
  hideMcpProtocolMethods?: boolean;
}): boolean {
  if (!isAssetsComponentsTabItem(component)) {
    return false;
  }

  if (!isMcpApplication) {
    return true;
  }

  if (component.type === 'MCP_PROTOCOL') {
    if (hideMcpProtocolMethods) {
      return false;
    }

    return isMcpProtocolAssetVisible(component);
  }

  return isToolFamilyComponentType(component.type);
}

export function isComponentsTabListItem({
  component,
  isMcpApplication = false,
  codeCoverageFilter = '',
  hideMcpProtocolMethods,
}: {
  component: AiComponentDto;
  isMcpApplication?: boolean;
  codeCoverageFilter?: AiComponentCodeCoverageFilter;
  hideMcpProtocolMethods?: boolean;
}): boolean {
  return (
    isComponentsTabVisibleItem({
      component,
      isMcpApplication,
      hideMcpProtocolMethods,
    }) && isAiComponentVisibleInAssets({ component, codeCoverageFilter })
  );
}

export function countComponentsTabListItems({
  components,
  isMcpApplication = false,
  codeCoverageFilter = '',
  hideMcpProtocolMethods,
}: {
  components: AiComponentDto[];
  isMcpApplication?: boolean;
  codeCoverageFilter?: AiComponentCodeCoverageFilter;
  hideMcpProtocolMethods?: boolean;
}): number {
  return components.filter((component) =>
    isComponentsTabListItem({
      component,
      isMcpApplication,
      codeCoverageFilter,
      hideMcpProtocolMethods,
    }),
  ).length;
}

export function matchesMcpComponentTypeFilter({
  componentType,
  filterType,
  isMcpApplication,
}: {
  componentType: AiComponentDto['type'];
  filterType: string;
  isMcpApplication: boolean;
}): boolean {
  if (!filterType) {
    return true;
  }

  if (componentType === filterType) {
    return true;
  }

  if (
    isMcpApplication &&
    filterType === 'MCP_TOOL' &&
    componentType === 'TOOL'
  ) {
    return true;
  }

  return false;
}
